/**
 * Lediga uppdrag — client-side search, filtering, sorting, pagination and
 * saved-ads for the assignment list (views/components/lediga-uppdrag.blade.php).
 *
 * Every assignment is rendered server-side, soonest application deadline
 * first; its filterable values live in data attributes on the card. The
 * sort dropdown switches between newest published/republished first
 * (the default) and soonest deadline, undated last.
 * Saved ads persist in localStorage; ids of assignments that are no
 * longer listed (archived) are pruned on load.
 *
 * Each filter is a custom multi-select: a pill-shaped trigger that opens a
 * panel of checkboxes. Values checked within one filter are OR:ed, and the
 * filters themselves are AND:ed. The dropdowns only take effect once the
 * "Filtrera" button is pressed: their applied values are held in
 * `appliedFilters`, which is what the matching reads, so a changed-but-
 * unsubmitted dropdown never leaks into the results. Search, sorting and
 * the saved-ads toggle apply immediately.
 *
 * Search, filter, sort, page and saved-only state is mirrored to the URL
 * query string (pushState) so results can be shared/bookmarked and the
 * back button steps through changes. Typing in the search field uses
 * replaceState to avoid flooding the history with one entry per keystroke.
 */

const STORAGE_KEY = 'upgraded-saved-uppdrag';
const PER_PAGE = 10;

export function initLedigaUppdrag() {
    document.querySelectorAll('.lediga-uppdrag').forEach((root) => {
        const list = root.querySelector('[data-uppdrag-list]');
        const cards = Array.from(root.querySelectorAll('[data-uppdrag-card]'));
        const searchInput = root.querySelector('[data-uppdrag-search]');
        const sortSelect = root.querySelector('[data-uppdrag-sort]');
        const filterForm = root.querySelector('[data-uppdrag-filters]');
        const filterToggle = root.querySelector('[data-uppdrag-filter-toggle]');
        const filterBody = root.querySelector('[data-uppdrag-filter-body]');
        const filterClear = root.querySelector('[data-uppdrag-filter-clear]');
        const filterGroups = Array.from(root.querySelectorAll('[data-uppdrag-filter]'));
        const savedToggle = root.querySelector('[data-uppdrag-saved-toggle]');
        const savedCount = root.querySelector('[data-uppdrag-saved-count]');
        const pagination = root.querySelector('[data-uppdrag-pagination]');
        const emptyMessage = root.querySelector('[data-uppdrag-empty]');

        if (!list || !cards.length) {
            return;
        }

        // Server order: ascending deadline, undated last. The default sort
        // is newest published first, so `apply()` re-sorts on first render.
        const initialOrder = new Map(cards.map((card, index) => [card, index]));
        const isNewestFirst = () => sortSelect?.value !== 'stigande';

        let page = 1;
        let savedOnly = false;

        // The submitted dropdown values (filter key -> array of checked
        // values). Matching reads these rather than the checkboxes
        // themselves, so a dropdown changed without pressing "Filtrera"
        // never leaks into the results when something else (search, sort,
        // saved ads) triggers a re-render.
        const appliedFilters = new Map();

        const checkboxesOf = (group) => Array.from(group.querySelectorAll('input[type="checkbox"]'));
        const checkedValues = (group) =>
            checkboxesOf(group)
                .filter((box) => box.checked)
                .map((box) => box.value);

        // Trigger text: the "Alla ..." label when nothing is checked,
        // otherwise the checked option names.
        const paintFilterSummary = (group) => {
            const summary = group.querySelector('[data-uppdrag-filter-summary]');
            if (!summary) {
                return;
            }
            const names = checkboxesOf(group)
                .filter((box) => box.checked)
                .map((box) => box.parentElement?.textContent.trim() || box.value);
            summary.textContent = names.length ? names.join(', ') : group.dataset.uppdragFilterAll || '';
        };

        const setFilterOpen = (group, open) => {
            const trigger = group.querySelector('[data-uppdrag-filter-trigger]');
            const options = group.querySelector('[data-uppdrag-filter-options]');
            if (!trigger || !options) {
                return;
            }
            options.hidden = !open;
            trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        };

        const closeAllFilters = (except = null) => {
            filterGroups.forEach((group) => {
                if (group !== except) {
                    setFilterOpen(group, false);
                }
            });
        };

        const commitFilters = () => {
            filterGroups.forEach((group) =>
                appliedFilters.set(group.dataset.uppdragFilter, checkedValues(group))
            );
        };

        const setSavedOnly = (value) => {
            savedOnly = value;
            if (!savedToggle) {
                return;
            }
            savedToggle.setAttribute('aria-pressed', value ? 'true' : 'false');
            savedToggle.classList.toggle('bg-blue', value);
            savedToggle.classList.toggle('text-white', value);
            savedToggle.classList.toggle('bg-white', !value);
            savedToggle.classList.toggle('text-blue', !value);
        };

        // Restore search/sort/filter/page/saved state from the query string.
        const readUrlState = () => {
            const params = new URLSearchParams(window.location.search);
            if (searchInput) {
                searchInput.value = params.get('sok') || '';
            }
            if (sortSelect) {
                sortSelect.value = params.get('sortering') === 'stigande' ? 'stigande' : 'fallande';
            }
            filterGroups.forEach((group) => {
                const values = (params.get(group.dataset.uppdragFilter) || '').split(',').filter(Boolean);
                checkboxesOf(group).forEach((box) => {
                    box.checked = values.includes(box.value);
                });
                paintFilterSummary(group);
            });
            setSavedOnly(params.get('sparade') === '1');
            page = Math.max(1, parseInt(params.get('sida'), 10) || 1);
            // The URL only ever holds submitted filters, so restoring it
            // counts as pressing "Filtrera".
            commitFilters();
        };

        // Mirror the current state to the URL. Defaults are omitted so a
        // pristine state leaves the URL untouched.
        const writeUrlState = (push = true) => {
            const params = new URLSearchParams(window.location.search);
            const set = (key, value) => (value ? params.set(key, value) : params.delete(key));

            set('sok', (searchInput?.value || '').trim());
            set('sortering', isNewestFirst() ? '' : 'stigande');
            appliedFilters.forEach((values, key) => set(key, values.join(',')));
            set('sparade', savedOnly ? '1' : '');
            set('sida', page > 1 ? String(page) : '');

            const query = params.toString();
            if (query === new URLSearchParams(window.location.search).toString()) {
                return;
            }
            const url = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
            history[push ? 'pushState' : 'replaceState'](null, '', url);
        };

        const readSaved = () => {
            try {
                const ids = JSON.parse(localStorage.getItem(STORAGE_KEY));
                return new Set(Array.isArray(ids) ? ids : []);
            } catch {
                return new Set();
            }
        };
        const saved = readSaved();
        const writeSaved = () => {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify([...saved]));
            } catch {
                // Storage unavailable (private mode, quota): keep in memory only.
            }
        };

        // Every published assignment is rendered, so a saved id without a
        // card belongs to one that has since been archived or deleted.
        // Drop it, or it would count towards "Sparade annonser" without
        // ever showing up in the saved-only list.
        const cardIds = new Set(cards.map((card) => card.dataset.id));
        const staleIds = [...saved].filter((id) => !cardIds.has(id));
        if (staleIds.length) {
            staleIds.forEach((id) => saved.delete(id));
            writeSaved();
        }

        const paintSaved = (card) => {
            const isSaved = saved.has(card.dataset.id);
            card.querySelector('[data-uppdrag-heart-outline]')?.classList.toggle('hidden', isSaved);
            card.querySelector('[data-uppdrag-heart-solid]')?.classList.toggle('hidden', !isSaved);
        };

        const updateSavedCount = () => {
            if (savedCount) {
                savedCount.textContent = String(saved.size);
            }
        };

        const matches = (card) => {
            if (savedOnly && !saved.has(card.dataset.id)) {
                return false;
            }

            const words = (searchInput?.value || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
            const haystack = card.dataset.search || '';
            if (!words.every((word) => haystack.includes(word))) {
                return false;
            }

            return [...appliedFilters].every(([key, selected]) => {
                if (!selected.length) {
                    return true;
                }
                const values = (card.dataset[key] || '').split(' ').filter(Boolean);
                return selected.some((value) => values.includes(value));
            });
        };

        const pageButton = (label, disabled, onClick) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className =
                'flex shrink-0 items-center justify-center size-11 bg-white border border-grey-500 rounded-[1.25rem] ' +
                'drop-shadow-[0_0.25rem_0.125rem_rgba(0,0,0,0.25)] text-blue cursor-pointer disabled:opacity-40 disabled:cursor-default';
            button.disabled = disabled;
            button.setAttribute('aria-label', label);
            button.innerHTML =
                label === 'prev'
                    ? '<svg class="size-5" viewBox="0 0 320 512" fill="currentColor" aria-hidden="true"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L105.4 256 246.6 114.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"/></svg>'
                    : '<svg class="size-5" viewBox="0 0 320 512" fill="currentColor" aria-hidden="true"><path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/></svg>';
            button.addEventListener('click', onClick);
            return button;
        };

        // Scroll so the search field is visible above the new page, offset
        // for the sticky header (which would otherwise cover the top of it).
        const scrollToSearch = () => {
            const header = document.querySelector('header.main-header');
            const offset = (header?.offsetHeight ?? 0) + 24;
            const target = root.querySelector('label[for="uppdrag-search"]') ?? searchInput ?? root;
            const top = target.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        };

        const goToPage = (target) => {
            page = target;
            apply();
            writeUrlState();
            scrollToSearch();
        };

        const NUMBER_CLASS = 'min-w-6 md:min-w-8 text-center font-roboto text-2xl leading-[1.5] text-black';

        const numberButton = (n) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = String(n);
            button.className =
                `${NUMBER_CLASS} bg-transparent border-0 p-0 cursor-pointer ` +
                (n === page ? 'font-bold' : 'font-normal');
            if (n === page) {
                button.setAttribute('aria-current', 'page');
            }
            button.addEventListener('click', () => goToPage(n));
            return button;
        };

        const ellipsis = () => {
            const span = document.createElement('span');
            span.textContent = '\u2026';
            span.className = NUMBER_CLASS;
            span.setAttribute('aria-hidden', 'true');
            return span;
        };

        // Same windowing as Components::pagination(): first and last page,
        // the current page and its neighbours; longer runs of skipped pages
        // collapse to an ellipsis, a run of a single page shows that page.
        const renderPagination = (totalPages) => {
            if (!pagination) {
                return;
            }
            pagination.replaceChildren();
            pagination.hidden = totalPages <= 1;
            if (totalPages <= 1) {
                return;
            }

            pagination.appendChild(pageButton('prev', page === 1, () => goToPage(page - 1)));

            let prev = 0;
            for (let n = 1; n <= totalPages; n++) {
                const inWindow = Math.abs(n - page) <= 1;
                if (n !== 1 && n !== totalPages && !inWindow) {
                    continue;
                }
                if (n - prev === 2) {
                    pagination.appendChild(numberButton(n - 1));
                } else if (n - prev > 2) {
                    pagination.appendChild(ellipsis());
                }
                pagination.appendChild(numberButton(n));
                prev = n;
            }

            pagination.appendChild(pageButton('next', page === totalPages, () => goToPage(page + 1)));
        };

        const apply = () => {
            // "Sista ansökningsdatum" keeps the server order (soonest
            // deadline first, undated last). "Nyast först" (the default)
            // sorts by the published date, newest first; 
            // Cards are re-appended in the new order.
            const sorted = [...cards].sort((a, b) => {
                if (!isNewestFirst()) {
                    return initialOrder.get(a) - initialOrder.get(b);
                }
                const aDate = a.dataset.published || '';
                const bDate = b.dataset.published || '';
                return bDate.localeCompare(aDate) || initialOrder.get(a) - initialOrder.get(b);
            });
            sorted.forEach((card) => list.appendChild(card));
            if (emptyMessage) {
                list.appendChild(emptyMessage);
            }

            const visible = sorted.filter(matches);
            const totalPages = Math.max(1, Math.ceil(visible.length / PER_PAGE));
            page = Math.min(page, totalPages);

            cards.forEach((card) => {
                card.hidden = true;
            });
            visible.slice((page - 1) * PER_PAGE, page * PER_PAGE).forEach((card) => {
                card.hidden = false;
            });

            if (emptyMessage) {
                emptyMessage.hidden = visible.length > 0;
            }
            renderPagination(totalPages);
        };

        cards.forEach((card) => {
            paintSaved(card);
            card.querySelector('[data-uppdrag-save]')?.addEventListener('click', () => {
                const id = card.dataset.id;
                if (saved.has(id)) {
                    saved.delete(id);
                } else {
                    saved.add(id);
                }
                writeSaved();
                paintSaved(card);
                updateSavedCount();
                if (savedOnly) {
                    apply();
                }
            });
        });

        savedToggle?.addEventListener('click', () => {
            setSavedOnly(!savedOnly);
            page = 1;
            apply();
            writeUrlState();
        });

        // Applies as you type; the native clear (x) fires "input" too.
        searchInput?.addEventListener('input', () => {
            page = 1;
            apply();
            writeUrlState(false);
        });

        sortSelect?.addEventListener('change', () => {
            apply();
            writeUrlState();
        });

        // Collapsible filter panel on mobile. The toggle is inert on
        // desktop (lg:pointer-events-none) where lg:flex keeps the body open.
        const setFilterExpanded = (expanded) => {
            if (!filterToggle || !filterBody) {
                return;
            }
            filterBody.classList.toggle('flex', expanded);
            filterBody.classList.toggle('hidden', !expanded);
            filterToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            filterToggle
                .querySelector('[data-uppdrag-filter-chevron]')
                ?.classList.toggle('rotate-180', expanded);
        };

        filterToggle?.addEventListener('click', () => {
            setFilterExpanded(filterToggle.getAttribute('aria-expanded') !== 'true');
        });

        // Filter dropdowns: the trigger toggles its own panel and closes
        // the others; clicking outside or pressing Escape closes all.
        filterGroups.forEach((group) => {
            const trigger = group.querySelector('[data-uppdrag-filter-trigger]');
            trigger?.addEventListener('click', () => {
                const open = trigger.getAttribute('aria-expanded') !== 'true';
                closeAllFilters(group);
                setFilterOpen(group, open);
            });
            group.addEventListener('change', () => paintFilterSummary(group));
            paintFilterSummary(group);
        });

        document.addEventListener('click', (event) => {
            if (!filterGroups.some((group) => group.contains(event.target))) {
                closeAllFilters();
            }
        });

        root.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                const open = filterGroups.find((group) => group.contains(document.activeElement));
                closeAllFilters();
                open?.querySelector('[data-uppdrag-filter-trigger]')?.focus();
            }
        });

        filterForm?.addEventListener('submit', (event) => {
            event.preventDefault();
            closeAllFilters();
            commitFilters();
            page = 1;
            apply();
            writeUrlState();
            // On mobile, fold the panel away and jump to the results.
            if (filterToggle && getComputedStyle(filterToggle).pointerEvents !== 'none') {
                setFilterExpanded(false);
                list.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });

        // "Rensa alla filter": uncheck everything and apply immediately.
        filterClear?.addEventListener('click', () => {
            closeAllFilters();
            filterGroups.forEach((group) => {
                checkboxesOf(group).forEach((box) => {
                    box.checked = false;
                });
                paintFilterSummary(group);
            });
            commitFilters();
            page = 1;
            apply();
            writeUrlState();
        });

        // Back/forward: restore the state the URL describes.
        window.addEventListener('popstate', () => {
            readUrlState();
            apply();
        });

        readUrlState();
        updateSavedCount();
        apply();
    });
}
